// PrivacyPolicy.jsx
import { useState } from "react";
import Logo from "../assets/logo.webp";
const PrivacyPolicy = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-black text-white min-h-screen font-sans">
      {/* NAVBAR */}
      <nav className="w-full border-b border-white/10 px-10 lg:px-6 md:px-5 sm:px-4 py-4 flex items-center justify-between">
        <a href="https://vupop.io/" className="-m-1.5 p-1.5">
          <img
            src={Logo}
            alt="logo"
            className="w-24 sm:w-24 md:w-32 lg:w-36 h-auto"
          />
        </a>

        {/* Desktop */}
        <div className="flex items-center gap-6 md:hidden">
          <a
            href="https://manager.vupop.io/broadcaster/login"
            className="text-white hover:text-[#F8FD24] transition"
          >
            Login
          </a>
          <a
            href="https://manager.vupop.io/broadcaster/signup/subscription"
            className="bg-[#F8FD24] text-black px-4 py-2 rounded-md font-semibold hover:opacity-90 transition"
          >
            Sign Up
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="hidden md:block text-white"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ☰
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:block hidden px-6 pb-4 border-b border-white/10">
          <a
            href="https://manager.vupop.io/broadcaster/login"
            className="block w-full text-left py-2"
          >
            Login
          </a>
          <a
            href="https://manager.vupop.io/broadcaster/signup/subscription"
            className="block w-full text-left py-2 text-[#F8FD24]"
          >
            Sign Up
          </a>
        </div>
      )}

      {/* CONTENT */}
      <div className="px-10 lg:px-6 md:px-5 sm:px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* HEADER CARD */}
          <div className="border border-white/10 rounded-xl p-6 mb-10 bg-white/5">
            <h1 className="text-4xl lg:text-3xl md:text-2xl font-bold text-[#F8FD24] mb-4">
              VUPOP Privacy and Cookie Policy
            </h1>

            <p className="mb-2">Last updated: 9 April 2026</p>
            <p>
              Applies to: VUPOP websites, landing pages, web dashboards, and
              mobile application services
            </p>
          </div>

          {/* QUICK ANSWERS CARD */}
          <div className="border border-white/10 rounded-xl p-6 mb-10">
            <h2 className="text-2xl md:text-xl font-semibold text-[#F8FD24] mb-4">
              Quick Answers for Clients
            </h2>

            <p className="mb-2 font-semibold">
              Will your software deploy cookies or similar tracking technologies
              and if so, how is user consent managed?
            </p>
            <p className="mb-4 text-white/70">
              Yes, on web properties only (website, landing pages, and web
              dashboards), VUPOP uses cookies and similar tracking technologies
              for analytics and service operation, including Google Analytics
              and Microsoft Clarity.
            </p>

            <p className="mb-4 text-white/70">
              The VUPOP mobile app does not use browser cookies.
            </p>

            <p className="mb-6 text-white/70">
              Where required by applicable law, consent for non-essential
              cookies/tracking technologies is managed through web cookie
              consent controls. Essential cookies required for core
              service/security functions may remain active without opt-in where
              legally permitted.
            </p>
            <hr className="mb-5 border-gray-600" />
            <p className="mb-2 font-semibold">
              What is your retention period of data?
            </p>

            <p className="mb-4 text-white/70">
              VUPOP applies retention based on data type, legal requirements,
              and service needs, generally retaining data for the maximum
              periods permitted by applicable systems and configurations unless
              deletion is required by law or valid data-rights requests:
            </p>

            <ul className="list-disc ml-6 space-y-2 text-white/70">
              <li>
                <b>Google Analytics (GA4) web analytics data:</b> retained
                according to GA4 retention controls (typically up to 14 months
                for user/event-level data in standard GA4 properties).
              </li>
              <li>
                <b>Microsoft Clarity data:</b> playback/session recording data
                is retained for around 30 days; click/heatmap and
                labeled/favorited session data are retained for up to 13 months.
              </li>
              <li>
                <b>Internal Firebase-based analytics data:</b> retained on an
                ongoing basis for internal analytics and reporting, including
                indefinite retention where configured, subject to legal
                obligations and valid data-rights requests.
              </li>
              <li>
                <b>Other service data:</b> retained as needed for operational,
                contractual, and legal purposes, then deleted, anonymised, or
                restricted as appropriate.
              </li>
            </ul>
          </div>

          {/* Sections */}
          <Section title="1. Who We Are">
            This Privacy and Cookie Policy explains how Social on Air Ltd
            (trading as VUPOP, "we", "us", "our") collects, uses, stores, and
            shares personal data when you use our services.
            <br />
            <br />
            Social on Air Ltd is the data controller for the purposes of
            applicable data protection law.
            <br />
            <br />
            If you have questions about this policy or your data, contact:{" "}
            <a
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  "https://mail.google.com/mail/?view=cm&fs=1&to=contact@vupop.io",
                  "_blank",
                );
              }}
              className="underline text-yellow-400 hover:text-yellow-300 cursor-pointer"
            >
              privacy@vupop.io
            </a>
          </Section>

          <Section title="2. Scope">
            This policy applies to:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>VUPOP public websites and landing pages</li>
              <li>VUPOP web platforms and dashboards</li>
              <li>VUPOP mobile app services</li>
            </ul>
            <br />
            This policy does not apply to third-party services that are not
            operated by VUPOP, even if linked from our services.
          </Section>

          <Section title="3. Data We Collect">
            Depending on how you use VUPOP, we may collect:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>
                Account and profile data (for example name, email, account
                identifiers)
              </li>
              <li>
                Authentication and security data (for example login events,
                device/session signals)
              </li>
              <li>
                Content and usage data (for example uploaded media metadata,
                in-product actions, feature usage)
              </li>
              <li>
                Device and technical data (for example browser/device type, IP
                address, OS version, app version)
              </li>
              <li>
                Payment and billing data necessary to provide services
                (processed with payment partners)
              </li>
              <li>
                Support and communications data (for example messages sent to
                support)
              </li>
            </ul>
            <br />
            We do not require special category/sensitive personal data for
            standard use of the platform.
          </Section>

          <Section title="4. How We Use Data">
            We use personal data to:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>Provide and operate VUPOP services</li>
              <li>Authenticate users and secure accounts</li>
              <li>
                Process transactions and support platform billing/payout
                operations
              </li>
              <li>
                Maintain platform security, detect abuse/fraud, and troubleshoot
                issues
              </li>
              <li>Improve product performance and user experience</li>
              <li>Generate analytics and service reporting</li>
              <li>
                Comply with legal obligations and enforce applicable terms
              </li>
            </ul>
          </Section>

          <Section title="5. Cookies and Tracking (Summary)">
            VUPOP uses cookies <b>only on web properties</b> (not the mobile
            app).
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>
                Essential cookies: required for platform functionality and
                security
              </li>
              <li>
                Non-essential cookies: used for analytics (Google Analytics,
                Microsoft Clarity)
              </li>
              <li>
                Consent: users can accept or reject non-essential cookies via
                cookie controls
              </li>
            </ul>
            <br />
            The mobile app does not use browser cookies.
          </Section>

          <Section title="6. Analytics and Tracking">
            <p className="font-semibold text-lg">
              6.1{"  "} Web (sites, landing pages, dashboards)
            </p>
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>Google Analytics</li>
              <li>Microsoft Clarity</li>
            </ul>
            <br />
            These tools may use cookies and similar technologies on web
            properties.
            <br />
            <br />
            <p className="font-semibold text-lg">6.2 Mobile app</p>
            <br />
            The VUPOP mobile app does not use browser cookies.
            <br />
            <br />
            For app analytics, we use <b> custom app analytics</b> (non-cookie
            telemetry and event data).
          </Section>

          <Section title="7. Cookie Use">
            Cookies are used on VUPOP web properties (not the mobile app) for
            purposes such as:
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Core website/platform functionality</li>
              <li>Security and fraud-prevention support</li>
              <li>Analytics and product performance insights</li>
            </ul>
            <br />
            Where required by law, users are offered controls to manage
            non-essential cookie preferences.
            <br />
            <br />
            Non-essential cookies/tracking technologies are managed via cookie
            consent controls on web properties.
          </Section>

          <Section title="8. Legal Bases (UK/EU)">
            Where UK/EU data protection law applies, we process data under one
            or more of:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>Performance of a contract</li>
              <li>
                Legitimate interests (for example service security, operations,
                and improvement)
              </li>
              <li>Legal obligations</li>
              <li>
                Consent (where required, such as certain non-essential
                cookie/analytics processing)
              </li>
            </ul>
          </Section>

          <Section title="9. Data Retention">
            We retain data according to business and legal requirements,
            generally for the maximum periods supported by our systems and
            configurations, including:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>
                <b>User usage data:</b> retained on an ongoing basis to support
                platform operations, analytics, service integrity, and
                historical reporting, unless deletion is required by law or
                valid data-rights request.
              </li>
              <li>
                <b>Google Analytics (GA4):</b> retained according to GA4
                retention controls (typically up to 14 months for
                user/event-level data in standard GA4 properties).
              </li>
              <li>
                <b>Microsoft Clarity:</b> recording playback data retained for
                around 30 days; click/heatmap and labeled or favorited session
                data retained for up to 13 months.
              </li>
              <li>
                <b>Internal Firebase-based analytics data:</b> retained on an
                ongoing basis, including indefinite retention where configured
                for internal analytics and reporting, subject to legal
                obligations and valid rights requests.
              </li>
            </ul>
            <br />
            Where deletion, anonymisation, or restriction is required by law or
            valid request, we apply that process as appropriate.
          </Section>

          <Section title="10. Sharing of Data">
            We may share personal data with trusted service providers where
            needed to deliver and operate the service, including categories such
            as:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>Hosting and infrastructure providers</li>
              <li>Analytics providers</li>
              <li>Payment and billing processors</li>
              <li>Customer support and operational tooling providers</li>
            </ul>
            <br />
            We may also share data where required by law, regulation, legal
            process, or to protect rights, safety, and platform integrity.
            <br />
            <br />
            We do not sell personal data in the ordinary meaning of "sell."
          </Section>

          <Section title="11. International Transfers">
            Where personal data is transferred across borders, we use
            appropriate safeguards required by applicable law.
          </Section>

          <Section title="12. Security">
            We use technical and organisational safeguards designed to protect
            personal data.
            <br />
            <br />
            No system can guarantee absolute security, but we continuously
            monitor and improve our controls.
          </Section>

          <Section title="13. Your Rights">
            Depending on your location and applicable law, you may have rights
            to:
            <br />
            <br />
            <ul className="list-disc ml-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete data</li>
              <li>Restrict or object to certain processing</li>
              <li>Withdraw consent where processing relies on consent</li>
              <li>Request data portability</li>
            </ul>
            <br />
            To make a request, contact:{" "}
            <a
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  "https://mail.google.com/mail/?view=cm&fs=1&to=contact@vupop.io",
                  "_blank",
                );
              }}
              className="underline text-yellow-400 hover:text-yellow-300 cursor-pointer"
            >
              privacy@vupop.io
            </a>
            <br />
            <br />
            We will review and respond in line with applicable data protection
            laws.
          </Section>

          <Section title="14. Children">
            VUPOP services are not intended for children under 18, and we do not
            knowingly collect personal data from children under 18.
          </Section>

          <Section title="15. Changes to This Policy">
            We may update this policy from time to time.
            <br />
            <br />
            If we make material changes, we will update the date above and
            publish the updated version on applicable VUPOP properties.
          </Section>

          <Section title="16. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?">
            If you have questions or comments about this notice, you may contact
            our Data Protection Officer (DPO) by email at{" "}
            <a
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  "https://mail.google.com/mail/?view=cm&fs=1&to=contact@vupop.io",
                  "_blank",
                );
              }}
              className="underline text-yellow-400 hover:text-yellow-300 cursor-pointer"
            >
              privacy@vupop.io
            </a>
            , or contact us by post at:
            <br />
            <br />
            Social on Air Ltd
            <br />
            (VUPOP is a trading name of Social on Air Ltd)
            <br />
            Data Protection Officer
            <br />
            11a Abbey Road
            <br />
            Malvern, Worcestershire WR14 3EA
            <br />
            United Kingdom
          </Section>
        </div>
      </div>
      <footer className="border-t border-white/10 mt-10 px-10 lg:px-6 md:px-5 sm:px-4 py-8 text-sm">
        <div className="max-w-5xl mx-auto space-y-2 text-white/70">
          <p>Last updated: 9 April 2026</p>
          <p>
            Applies to: VUPOP websites, landing pages, web dashboards, and
            mobile application services
          </p>
          <p className="text-[#F8FD24]">© Social on Air Ltd (VUPOP)</p>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl md:text-xl font-semibold text-[#F8FD24] mb-4">
      {title}
    </h2>
    <p className="text-white text-base md:text-sm leading-7 whitespace-pre-line">
      {children}
    </p>
  </div>
);

export default PrivacyPolicy;
